import type { SupportV2EntityParitySnapshot } from "@redotech/admin-sdk/rpc/schema/support/get-support-v2-entity-parity";
import { RedoBadge } from "@redotech/arbiter/badge/redo-badge";
import { RedoCollapseSubsection } from "@redotech/arbiter/collapse/redo-collapse-section";
import { Flex } from "@redotech/arbiter/flex";
import { Text } from "@redotech/arbiter/text";
import { memo } from "react";

/**
 * Shared renderer for a tag/macro/rule parity snapshot. Used by
 * the per-team migration card and the global migration-status table so both
 * surface reconciliation identically.
 */
export const ReconciliationResults = memo(function ReconciliationResults({
  snapshot,
}: {
  snapshot: SupportV2EntityParitySnapshot;
}) {
  return (
    <Flex dir="column" gap="sm">
      <RedoBadge
        color={snapshot.overallParity ? "success" : "error"}
        size="sm"
        text={snapshot.overallParity ? "In sync" : "Mismatch"}
      />
      {snapshot.results.map((result) => (
        <Flex dir="column" gap="xxs" key={result.entity}>
          <Flex align="center" gap="xs" wrap="wrap">
            <RedoBadge
              color={result.parity ? "success" : "error"}
              size="sm"
              text={result.entity}
            />
            <Text fontSize="xs" textColor="secondary">
              V1 {result.v1Count} → V2 {result.v2Count}
              {result.missingInV2.length > 0
                ? ` · ${result.missingInV2.length} missing in V2`
                : ""}
            </Text>
          </Flex>
          {result.missingInV2.length > 0 && (
            <RedoCollapseSubsection
              defaultCollapsed
              title={`${result.missingInV2.length} missing ${result.entity}`}
            >
              <Flex dir="column" gap="xxs" pl="lg">
                {result.missingInV2.map((miss) => (
                  <Text fontSize="xs" key={miss.key} textColor="secondary">
                    {miss.label}
                  </Text>
                ))}
              </Flex>
            </RedoCollapseSubsection>
          )}
        </Flex>
      ))}
      <Text fontSize="xs" textColor="secondary">
        Counts are raw totals; the badge reflects matched parity (by id/name).
        V2 may exceed V1 for entities that have native V2-only entries.
      </Text>
    </Flex>
  );
});
